import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { api, externalApi } from '../../../../services/authService';
import { selectCurrentToken } from '../../../../redux/authSlice';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import MessageTemplateService from '../../../../services/messageTemplateService';
import moment from 'moment-timezone';
import DripMessageConfig from '../../../../components/campaigns/drip/DripMessageConfig';
import DripScheduleConfig from '../../../../components/campaigns/drip/DripScheduleConfig';
import BasicInfoForm from '../../../../components/campaigns/BasicInfoForm';
import TestCampaignSection from '../../../../components/campaigns/TestCampaignSection';
import CampaignHeader from '../../../../components/campaigns/CampaignHeader';
import { validateStep } from '../../../../components/campaigns/CampaignValidation';
import CommunicationEndpointsProvider from '../../../../components/campaigns/CommunicationEndpointsProvider';
import OptOutConfig from '../../../../components/campaigns/OptOutConfig';
import { serializeDripCampaign } from '../../../../utils/campaignSerializers';
import RetryConfigurationPanel from '../../../../components/campaigns/RetryConfigurationPanel';
import { formatRetryConfigForBackend } from '../../../../utils/retryStrategyUtils';

// Get list of timezones from moment-timezone and format them for display
const timezones = moment.tz.names().map(zone => {
  const offset = moment.tz(zone).format('Z');
  const label = `(UTC${offset}) ${zone.replace(/_/g, ' ')}`;
  return {
    value: zone,
    label
  };
});

const steps = [
  'Basic Information',
  'Message Configuration',
  'Schedule Settings',
  'Test',
  'Review & Activate'
];

// Field mapping configuration
const FIELD_MAPPINGS = {
  // Basic fields
  name: 'name',
  description: 'description',
  account: 'account',
  crm_campaign: 'crm_campaign',
  start_date: 'start_date',
  end_date: 'end_date',
  is_ongoing: 'is_ongoing',
  active: 'active',
  channel: 'channel',
  template: 'template',
  status: 'status',
  auto_enroll_new_leads: 'auto_enroll_new_leads',
  auto_enroll_filters: 'auto_enroll_filters',
  lead_filters: 'lead_filters',

  // Channel config fields
  email_config: {
    content: 'email_config.content',
    subject: 'email_config.subject',
    from_endpoint: 'email_config.from_endpoint',
    from_name: 'email_config.from_name',
    reply_to: 'email_config.reply_to',
    priority: 'email_config.priority',
    track_opens: 'email_config.track_opens',
    track_clicks: 'email_config.track_clicks',
    attachments: 'email_config.attachments'
  },
  sms_config: {
    content: 'sms_config.content',
    from_endpoint: 'sms_config.from_endpoint',
    priority: 'sms_config.priority',
    track_delivery: 'sms_config.track_delivery',
    track_replies: 'sms_config.track_replies',
    media_urls: 'sms_config.media_urls'
  },
  voice_config: {
    content: 'voice_config.content',
    from_endpoint: 'voice_config.from_endpoint',
    voice: 'voice_config.voice',
    language: 'voice_config.language',
    priority: 'voice_config.priority',
    retry_attempts: 'voice_config.retry_attempts',
    retry_delay: 'voice_config.retry_delay',
    record_call: 'voice_config.record_call',
    call_timeout: 'voice_config.call_timeout',
    machine_detection: 'voice_config.machine_detection'
  },
  chat_config: {
    content: 'chat_config.content',
    from_endpoint: 'chat_config.from_endpoint',
    platform: 'chat_config.platform',
    priority: 'chat_config.priority',
    track_delivery: 'chat_config.track_delivery',
    track_read: 'chat_config.track_read',
    media_urls: 'chat_config.media_urls',
    quick_replies: 'chat_config.quick_replies'
  },
  drip_schedule: {
    business_hours_only: 'drip_schedule.business_hours_only',
    start_time: 'drip_schedule.start_time',
    end_time: 'drip_schedule.end_time',
    exclude_weekends: 'drip_schedule.exclude_weekends',
    timezone: 'drip_schedule.timezone'
  }
};

// Channel configuration utilities
const getDefaultChannelConfig = (channel) => {
  const defaults = {
    email: {
      content: '',
      subject: '',
      from_endpoint: '',
      from_name: '',
      reply_to: '',
      priority: 'normal',
      track_opens: true,
      track_clicks: true,
      attachments: []
    },
    sms: {
      content: '',
      from_endpoint: '',
      priority: 'normal',
      track_delivery: true,
      track_replies: true,
      media_urls: []
    },
    voice: {
        content: '',
        from_endpoint: '',
        platform: 'bland_ai',
        voice_id: 'alexa',
        voice_name: 'Alexa (Female)',
        temperature: 0.7,
        max_duration: 5,
        retry_attempts: 3,
        voicemail_message: '',
        voicemail_action: 'hangup',
        record_call: false,
        call_timeout: 60,
      platform_config: {
        task: '',
        pathway_id: '',
        pathway_version: 1,
        first_sentence: '',
        background_track: '',
        model: 'base',
        wait_for_greeting: false,
        block_interruptions: false,
        interruption_threshold: 100,
        sensitive_voicemail_detection: false,
        transfer_phone_number: '',
        transfer_list: {},
        language: 'en-US',
        voicemail_sms: {},
        dispatch_hours: {},
        ignore_button_press: false,
        timezone: 'America/New_York',
        request_data: {},
        tools: [],
        start_time: '',
        voicemail_action: {},
        retry: { wait: 300, voicemail_action: 'leave_message' },
        max_duration: 5,
        record: false,
        webhook: '',
        webhook_events: [],
        metadata: {},
        citation_schema_id: '',
        analysis_preset: 'default',
        available_tags: [],
        geospatial_dialing: 'disabled',
        precall_dtmf_sequence: '',
        dynamic_data: [],
        keywords: [],
        pronunciation_guide: [],
        local_dialing: false
      },
      webhook_config: {
        webhook: '',
        webhook_events: []
      },
      metadata: {}
    },
    chat: {
      content: '',
      from_endpoint: '',
      platform: 'whatsapp',
      priority: 'normal',
      track_delivery: true,
      track_read: true,
      media_urls: [],
      quick_replies: []
    }
  };
  return defaults[channel] || {};
};

const getUpdatedChannelConfig = (channel, currentConfig, updates) => {
  const defaultConfig = getDefaultChannelConfig(channel);
  return {
    ...defaultConfig,
    ...currentConfig,
    ...updates
  };
};

const getStepChannelConfig = (channel, campaignConfig, stepConfig = {}) => {
  // Start with campaign-level config as base
  const baseConfig = { ...campaignConfig };
  
  // Only override specific fields that are explicitly set in step config
  const overrides = {};
  Object.entries(stepConfig).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      overrides[key] = value;
    }
  });

  return {
    ...baseConfig,
    ...overrides
  };
};

const normalizeChannelConfigs = (formData) => {
  const normalized = { ...formData };
  const channels = ['email', 'sms', 'voice', 'chat'];
  
  // Normalize main channel config
  const campaignChannelConfig = getUpdatedChannelConfig(
    formData.channel,
    normalized[`${formData.channel}_config`] || {},
    {}
  );
  normalized[`${formData.channel}_config`] = campaignChannelConfig;

  // Normalize message step channel configs
  if (normalized.drip_schedule?.message_steps) {
    normalized.drip_schedule.message_steps = normalized.drip_schedule.message_steps.map(step => {
      const updatedStep = { ...step };
      
      // Get step config using campaign config as base
      updatedStep[`${formData.channel}_config`] = getStepChannelConfig(
        formData.channel,
        campaignChannelConfig,
        step[`${formData.channel}_config`]
      );

      // Set other channel configs to null
      channels.forEach(ch => {
        if (ch !== formData.channel) {
          updatedStep[`${ch}_config`] = null;
        }
      });

      return updatedStep;
    });
  }

  return normalized;
};

const prepareSubmissionPayload = (formData) => {
  const normalizedFormData = normalizeChannelConfigs(formData);
  
  // Remove root-level voice_config for drip campaigns (only store at step level)
  const { voice_config, ...dataWithoutRootVoice } = normalizedFormData;
  
  // Use the new serializer for proper voice platform support
  const payload = serializeDripCampaign({
    ...dataWithoutRootVoice,
    start_date: normalizedFormData.start_date ? new Date(normalizedFormData.start_date).toISOString() : null,
    end_date: normalizedFormData.is_ongoing ? null : (normalizedFormData.end_date ? new Date(normalizedFormData.end_date).toISOString() : null)
  });

  // Add properly formatted retry configuration
  const retryConfig = formatRetryConfigForBackend(formData.retry_strategy_id, formData.max_retries);
  return {
    ...payload,
    ...retryConfig
  };
};

const CreateDripCampaign = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector(selectCurrentToken);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [crmCampaigns, setCrmCampaigns] = useState([]);
  const [statusChoices, setStatusChoices] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [communicationEndpoints, setCommunicationEndpoints] = useState([]);
  const [channelSwitchDialogOpen, setChannelSwitchDialogOpen] = useState(false);
  const [pendingChannel, setPendingChannel] = useState(null);
  const [previousChannelState, setPreviousChannelState] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    account: '',
    crm_campaign: '',
    start_date: '',
    end_date: '',
    active: true,
    is_ongoing: false,
    campaign_type: 'drip',
    channel: 'email',
    template: '',
    content: '',
    status: 'draft',
    auto_enroll_new_leads: false,
    auto_enroll_filters: [],
    // Retry configuration
    retry_strategy_id: null,
    max_retries: null,
    email_config: {
      content: '',
      subject: '',
      from_endpoint: '',
      from_name: '',
      reply_to: '',
      priority: 'normal',
      track_opens: true,
      track_clicks: true,
      attachments: []
    },
    sms_config: {
      content: '',
      from_endpoint: '',
      priority: 'normal',
      track_delivery: true,
      track_replies: true,
      media_urls: []
    },
    voice_config: {
      content: '',
      from_endpoint: '',
      platform: 'bland_ai',
      voice_id: 'alexa',
      voice_name: 'Alexa (Female)',
      temperature: 0.7,
      max_duration: 5,
      retry_attempts: 3,
      voicemail_message: '',
      voicemail_action: 'hangup',
      record_call: false,
      call_timeout: 60,
      platform_config: {
        task: '',
        pathway_id: '',
        pathway_version: 1,
        first_sentence: '',
        background_track: '',
        model: 'base',
        wait_for_greeting: false,
        block_interruptions: false,
        interruption_threshold: 100,
        sensitive_voicemail_detection: false,
        transfer_phone_number: '',
        transfer_list: {},
        language: 'en-US',
        voicemail_sms: {},
        dispatch_hours: {},
        ignore_button_press: false,
        timezone: 'America/New_York',
        request_data: {},
        tools: [],
        start_time: '',
        voicemail_action: {},
        retry: { wait: 300, voicemail_action: 'leave_message' },
        max_duration: 5,
        record: false,
        webhook: '',
        webhook_events: [],
        metadata: {},
        citation_schema_id: '',
        analysis_preset: 'default',
        available_tags: [],
        geospatial_dialing: 'disabled',
        precall_dtmf_sequence: '',
        dynamic_data: [],
        keywords: [],
        pronunciation_guide: [],
        local_dialing: false
      },
      webhook_config: {
        webhook: '',
        webhook_events: []
      },
      metadata: {}
    },
    chat_config: {
      content: '',
      from_endpoint: '',
      platform: 'whatsapp',
      priority: 'normal',
      track_delivery: true,
      track_read: true,
      media_urls: [],
      quick_replies: []
    },
    drip_schedule: {
      business_hours_only: true,
      start_time: '09:00',
      end_time: '17:00',
      exclude_weekends: true,
      timezone: 'UTC',
      message_steps: [
        {
          order: 1,
          delay_units: 24,
          delay_unit_type: 'hours',
          template: null,
          content: null
        }
      ]
    },
    lead_filters: [],
    enable_opt_out: true,
    initial_opt_out_notice: "Reply STOP to opt out of further messages.",
    opt_out_message: "You’ve successfully opted out and will no longer receive messages from this number. Reply START at any time to resume."
  });

  const [testData, setTestData] = useState({
    email: '',
    phone: '',
    testStatus: null,
    testError: null,
    selectedDripStep: 0
  });

  // Load accounts, templates, and status choices on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accountsResponse, templatesResponse, statusResponse] = await Promise.all([
          api.get('/crm/accounts/'),
          MessageTemplateService.getTemplates({ channel: formData.channel }),
          api.get('/acs/nurturing-campaigns/status_choices/')
        ]);
        setAccounts(accountsResponse.data);
        setTemplates(templatesResponse);
        setStatusChoices(statusResponse.data);
      } catch (err) {
        setError('Failed to load required data');
        console.error('Error loading data:', err);
      }
    };
    fetchData();
  }, [formData.channel]);

  // Fetch CRM campaigns when account changes
  useEffect(() => {
    const fetchCrmCampaigns = async () => {
      if (formData.account) {
        try {
          const response = await api.get(`/crm/accounts/${formData.account}/campaigns/`);
          setCrmCampaigns(response.data);
        } catch (err) {
          setError('Failed to load CRM campaigns');
          console.error('Error loading CRM campaigns:', err);
        }
      } else {
        setCrmCampaigns([]);
      }
    };
    fetchCrmCampaigns();
  }, [formData.account]);

  const handleChannelChange = (newChannel) => {
    if (newChannel === formData.channel) return;

    // If there are existing message steps, show confirmation dialog
    if (formData.drip_schedule.message_steps.length > 0) {
      // Store current state before switching
      setPreviousChannelState({
        channel: formData.channel,
        channelConfig: formData[`${formData.channel}_config`],
        messageSteps: formData.drip_schedule.message_steps
      });
      setPendingChannel(newChannel);
      setChannelSwitchDialogOpen(true);
    } else {
      // If no message steps exist, switch channel directly
      switchChannel(newChannel);
    }
  };

  const switchChannel = (newChannel) => {
    // Reset channel-specific configs
    const newFormData = {
      ...formData,
      channel: newChannel,
      template: '', // Reset template selection
    };

    // Reset all channel configs
    ['email', 'sms', 'voice', 'chat'].forEach(channel => {
      newFormData[`${channel}_config`] = getDefaultChannelConfig(channel);
    });

    // Reset message steps to default with proper voice platform initialization
    const defaultStepConfig = getDefaultChannelConfig(newChannel);
    const newStep = {
      order: 1,
      delay_units: 24,
      delay_unit_type: 'hours',
      template: null,
      content: null,
      [`${newChannel}_config`]: {
        ...defaultStepConfig,
        from_endpoint: formData[`${newChannel}_config`]?.from_endpoint || '',
        from_endpoint_value: formData[`${newChannel}_config`]?.from_endpoint_value || ''
      }
    };

    // Add voice_config for voice channel
    if (newChannel === 'voice') {
      newStep.voice_config = {
        content: '',
        from_endpoint: formData[`${newChannel}_config`]?.from_endpoint || '',
        from_endpoint_value: formData[`${newChannel}_config`]?.from_endpoint_value || '',
        platform: formData[`${newChannel}_config`]?.platform || 'bland_ai',
        voice_id: formData[`${newChannel}_config`]?.voice_id || 'alexa',
        voice_name: formData[`${newChannel}_config`]?.voice_name || 'Alexa (Female)',
        temperature: formData[`${newChannel}_config`]?.temperature || 0.7,
        max_duration: formData[`${newChannel}_config`]?.max_duration || 5,
        retry_attempts: formData[`${newChannel}_config`]?.retry_attempts || 3,
        voicemail_message: formData[`${newChannel}_config`]?.voicemail_message || '',
        voicemail_action: formData[`${newChannel}_config`]?.voicemail_action || 'hangup',
        record_call: formData[`${newChannel}_config`]?.record_call ?? false,
        call_timeout: formData[`${newChannel}_config`]?.call_timeout || 60,
        platform_config: {
          task: '',
          pathway_id: '',
          pathway_version: 1,
          first_sentence: '',
          background_track: '',
          model: 'base',
          wait_for_greeting: false,
          block_interruptions: false,
          interruption_threshold: 100,
          sensitive_voicemail_detection: false,
          transfer_phone_number: '',
          transfer_list: {},
          language: 'en-US',
          voicemail_sms: {},
          dispatch_hours: {},
          ignore_button_press: false,
          timezone: 'America/New_York',
          request_data: {},
          tools: [],
          start_time: '',
          voicemail_action: {},
          retry: { wait: 300, voicemail_action: 'leave_message' },
          max_duration: 5,
          record: false,
          webhook: '',
          webhook_events: [],
          metadata: {},
          citation_schema_id: '',
          analysis_preset: 'default',
          available_tags: [],
          geospatial_dialing: 'disabled',
          precall_dtmf_sequence: '',
          dynamic_data: [],
          keywords: [],
          pronunciation_guide: [],
          local_dialing: false,
          ...formData[`${newChannel}_config`]?.platform_config
        },
        webhook_config: {
          webhook: '',
          webhook_events: [],
          ...formData[`${newChannel}_config`]?.webhook_config
        },
        metadata: formData[`${newChannel}_config`]?.metadata || {}
      };
    }

    newFormData.drip_schedule = {
      ...formData.drip_schedule,
      message_steps: [newStep]
    };

    setFormData(normalizeChannelConfigs(newFormData));
    setChannelSwitchDialogOpen(false);
    setPendingChannel(null);
  };

  const revertChannelSwitch = () => {
    if (previousChannelState) {
      const revertedFormData = {
        ...formData,
        channel: previousChannelState.channel,
        [`${previousChannelState.channel}_config`]: previousChannelState.channelConfig,
        drip_schedule: {
          ...formData.drip_schedule,
          message_steps: previousChannelState.messageSteps
        }
      };

      setFormData(normalizeChannelConfigs(revertedFormData));
      setPreviousChannelState(null);
    }
  };

  const updateNestedField = (obj, path, value) => {
    const keys = path.split('.');
    const updated = { ...obj };
    let temp = updated;
    keys.slice(0, -1).forEach(key => {
      temp[key] = { ...temp[key] };
      temp = temp[key];
    });
    temp[keys[keys.length - 1]] = value;
    return updated;
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;

    // Handle channel change specially
    if (name === 'channel') {
      handleChannelChange(value);
      return;
    }

    // Find the field mapping
    let fieldPath = null;
    
    // Check direct mappings first
    if (FIELD_MAPPINGS[name]) {
      fieldPath = typeof FIELD_MAPPINGS[name] === 'string' 
        ? FIELD_MAPPINGS[name] 
        : name;
    } else {
      // Check nested mappings
      for (const [configKey, configFields] of Object.entries(FIELD_MAPPINGS)) {
        if (typeof configFields === 'object' && configFields[name]) {
          fieldPath = configFields[name];
          break;
        }
      }
    }

    if (fieldPath) {
      setFormData(prev => {
        const newData = updateNestedField(prev, fieldPath, inputValue);
        return normalizeChannelConfigs(newData);
      });
    } else {
      console.warn(`No field mapping found for: ${name}`);
    }
  };

  const handleNext = () => {
    const { isValid, errors } = validateStep(activeStep, formData);
    if (!isValid) {
      setError(errors.join(', '));
      return;
    }
    setError(null);
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (activate = false) => {
    const { isValid, errors } = validateStep(activeStep, formData);
    if (!isValid) {
      setError(errors.join(', '));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = prepareSubmissionPayload({
        ...formData,
        status: activate ? 'active' : 'draft'
      });

      // Create the campaign
      const campaignResponse = await api.post('/acs/drip-campaigns/', payload);

      // If activating, make the activation API call
      if (activate) {
        await api.post(`/acs/nurturing-campaigns/${campaignResponse.data.id}/activate/`);
      }

      // Navigate to lead selection page
      navigate(`/crm/lead-nurturing/${campaignResponse.data.id}/lead-selection`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
      setCreateDialogOpen(false);
    }
  };

  const handleTestCampaign = async () => {
    setTestData(prev => ({ ...prev, testStatus: 'loading', testError: null }));
    try {
      const selectedStep = formData.drip_schedule.message_steps[testData.selectedDripStep];
      const channelConfig = selectedStep[`${formData.channel}_config`] || formData[`${formData.channel}_config`];
      
      if (formData.channel === 'voice') {
        // Handle voice testing - use step-level voice_config
        const stepVoiceConfig = selectedStep.voice_config || channelConfig;
        if (!stepVoiceConfig?.from_endpoint_value) {
          throw new Error('From endpoint is required');
        }

        const testPayload = {
          platform: stepVoiceConfig.platform || 'bland_ai',
          phone_number: testData.phone,
          from_endpoint: stepVoiceConfig.from_endpoint_value,
          // Platform-specific test data
          ...getPlatformTestPayload(stepVoiceConfig)
        };
        
        const response = await externalApi.post(`/communications/test-voice/${channelConfig.platform}/`, testPayload);
        
        if (response.data.success) {
          setTestData(prev => ({ 
            ...prev, 
            testStatus: 'success', 
            testError: null 
          }));
        } else {
          throw new Error(response.data.detail || 'Failed to send test voice call');
        }
      } else {
        // Handle other channels (SMS, email, chat)
        const message = selectedStep.template ? 
          templates.find(t => t.id === selectedStep.template)?.content : 
          selectedStep[`${formData.channel}_config`]?.content;

        if (!message) {
          throw new Error('Message content is required');
        }

        if (!channelConfig?.from_endpoint_value) {
          throw new Error('From endpoint is required');
        }

        const testPayload = {
          phone_number: testData.phone,
          message: message,
          twilio_number: channelConfig.from_endpoint_value
        };
        
        const response = await externalApi.post('/communications/send-direct-sms/', testPayload);
        
        if (response.data.success) {
          setTestData(prev => ({ 
            ...prev, 
            testStatus: 'success', 
            testError: null 
          }));
        } else {
          throw new Error(response.data.detail || 'Failed to send test message');
        }
      }
    } catch (err) {
      setTestData(prev => ({
        ...prev,
        testStatus: 'error',
        testError: err.response?.data?.detail || err.message || 'Failed to send test message'
      }));
    }
  };

  const getPlatformTestPayload = (channelConfig) => {
    const platform = channelConfig.platform || 'bland_ai';
    
    switch (platform) {
      case 'bland_ai':
        return {
          task: channelConfig.platform_config?.task || '',
          pathway_id: channelConfig.platform_config?.pathway_id || '',
          voice_id: channelConfig.voice_id || 'alexa',
          temperature: channelConfig.temperature || 0.7,
          max_duration: channelConfig.max_duration || 5
        };
      case 'vapi':
        return {
          voice_id: channelConfig.voice_id || 'shimmer',
          temperature: channelConfig.temperature || 0.7,
          max_duration: channelConfig.max_duration || 10
        };
      case 'elevenlabs':
        return {
          voice_id: channelConfig.voice_id || '21m00Tcm4TlvDq8ikWAM',
          temperature: channelConfig.temperature || 0.7,
          max_duration: channelConfig.max_duration || 5
        };
      case 'twilio':
        return {
          voice_id: channelConfig.voice_id || 'alexa'
        };
      default:
        return {};
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <BasicInfoForm
            formData={formData}
            onInputChange={handleInputChange}
            accounts={accounts}
            crmCampaigns={crmCampaigns}
            campaignTypes={{ drip: 'Drip Campaign' }}
          />
        );

      case 1:
        return (
          <CommunicationEndpointsProvider
            accountId={formData.account}
            campaignId={formData.crm_campaign}
            channel={formData.channel}
            activeTab={activeStep}
          >
            {({ endpoints, loading: endpointsLoading, error: endpointsError }) => (
              <Grid container spacing={3}>
                {endpointsError && (
                  <Grid item xs={12}>
                    <Alert severity="error">{endpointsError}</Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <DripMessageConfig
                    messageSteps={formData.drip_schedule.message_steps}
                    onMessageStepsChange={(steps) => {
                      setFormData(prev => ({
                        ...prev,
                        drip_schedule: {
                          ...prev.drip_schedule,
                          message_steps: steps
                        }
                      }));
                    }}
                    template={formData.template}
                    content={formData[`${formData.channel}_config`].content}
                    onTemplateChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        template: value,
                        [`${prev.channel}_config`]: {
                          ...prev[`${prev.channel}_config`],
                          content: value ? '' : prev[`${prev.channel}_config`].content
                        }
                      }));
                    }}
                    onContentChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        template: '',
                        [`${prev.channel}_config`]: {
                          ...prev[`${prev.channel}_config`],
                          content: value
                        }
                      }));
                    }}
                    templates={templates}
                    channel={formData.channel}
                    channelConfig={formData[`${formData.channel}_config`]}
                    onChannelConfigChange={(config) => {
                      setFormData(prev => ({
                        ...prev,
                        [`${prev.channel}_config`]: config
                      }));
                    }}
                    communicationEndpoints={endpoints}
                    onEndpointChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        [`${prev.channel}_config`]: {
                          ...prev[`${prev.channel}_config`],
                          from_endpoint: value
                        }
                      }));
                    }}
                    onChannelChange={handleChannelChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <OptOutConfig
                    config={{
                      enable_opt_out: formData.enable_opt_out,
                      initial_opt_out_notice: formData.initial_opt_out_notice,
                      opt_out_message: formData.opt_out_message
                    }}
                    onChange={(updatedConfig) => {
                      setFormData(prev => ({
                        ...prev,
                        ...updatedConfig
                      }));
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </CommunicationEndpointsProvider>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <DripScheduleConfig
                schedule={formData.drip_schedule}
                onScheduleChange={(schedule) => {
                  setFormData(prev => ({
                    ...prev,
                    drip_schedule: {
                      ...prev.drip_schedule,
                      ...schedule
                    }
                  }));
                }}
                timezones={timezones}
                timezoneComponent={
                  <Autocomplete
                    options={timezones}
                    getOptionLabel={(option) => option.label}
                    value={timezones.find(tz => tz.value === formData.drip_schedule.timezone) || null}
                    onChange={(_, newValue) => {
                      setFormData(prev => ({
                        ...prev,
                        drip_schedule: {
                          ...prev.drip_schedule,
                          timezone: newValue?.value || 'UTC'
                        }
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Timezone"
                        required
                        fullWidth
                        error={!formData.drip_schedule.timezone}
                        helperText={!formData.drip_schedule.timezone ? 'Timezone is required' : ''}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    groupBy={(option) => option.value.split('/')[0]}
                    renderGroup={(params) => (
                      <li key={params.group}>
                        <Box sx={{ 
                          p: 1, 
                          bgcolor: 'background.paper',
                          borderBottom: '1px solid',
                          borderColor: 'divider'
                        }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {params.group}
                          </Typography>
                        </Box>
                        <ul style={{ padding: 0, margin: 0 }}>
                          {params.children}
                        </ul>
                      </li>
                    )}
                  />
                }
              />
            </Grid>
            
            {/* Retry Configuration */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mt: 2 }}>
                <RetryConfigurationPanel
                  retryStrategy={formData.retry_strategy_id}
                  maxRetries={formData.max_retries}
                  onRetryStrategyChange={(strategyId) => setFormData(prev => ({ ...prev, retry_strategy_id: strategyId }))}
                  onMaxRetriesChange={(maxRetries) => setFormData(prev => ({ ...prev, max_retries: maxRetries }))}
                  description="Configure retry behavior for all drip messages (campaign-level default)"
                />
              </Paper>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <TestCampaignSection
            formData={formData}
            testData={testData}
            onTestDataChange={setTestData}
            onTestCampaign={handleTestCampaign}
            templates={templates}
          />
        );

      case 4:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Review Campaign Details
              </Typography>
              <Paper sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Basic Information</Typography>
                    <Typography>Name: {formData.name}</Typography>
                    <Typography>Description: {formData.description}</Typography>
                    <Typography>
                      Account:{' '}
                      {accounts.find((a) => a.id === formData.account)?.name}
                    </Typography>
                    <Typography>Campaign Type: Drip Campaign</Typography>
                    <Typography>
                      Channel: {formData.channel.charAt(0).toUpperCase() + formData.channel.slice(1)}
                    </Typography>
                    <Typography>
                      Start Date: {formData.start_date || 'Not set'}
                    </Typography>
                    <Typography>
                      End Date: {formData.is_ongoing ? 'Ongoing' : (formData.end_date || 'Not set')}
                    </Typography>
                    <Typography>Active: {formData.active ? 'Yes' : 'No'}</Typography>
                    <Typography>Status: {formData.status}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Message Configuration</Typography>
                    {formData.template ? (
                      <Typography>
                        Template:{' '}
                        {templates.find((t) => t.id === formData.template)?.name}
                      </Typography>
                    ) : (
                      <Typography>Content: {formData.content}</Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Channel Configuration</Typography>
                    {formData.channel === 'email' && (
                      <>
                        <Typography>From Email: {formData.email_config.from_endpoint}</Typography>
                        <Typography>From Name: {formData.email_config.from_name}</Typography>
                        <Typography>Reply To: {formData.email_config.reply_to}</Typography>
                        <Typography>Priority: {formData.email_config.priority}</Typography>
                        {formData.email_config.attachments.length > 0 && (
                          <Typography>
                            Attachments: {formData.email_config.attachments.map(a => a.name).join(', ')}
                          </Typography>
                        )}
                      </>
                    )}

                    {formData.channel === 'sms' && (
                      <>
                        <Typography>From Number: {formData.sms_config.from_endpoint}</Typography>
                        <Typography>Priority: {formData.sms_config.priority}</Typography>
                        {formData.sms_config.media_urls.length > 0 && (
                          <Typography>
                            Media URLs: {formData.sms_config.media_urls.length} attached
                          </Typography>
                        )}
                      </>
                    )}

                    {formData.channel === 'voice' && (
                      <>
                        <Typography>From Number: {formData.voice_config.from_endpoint}</Typography>
                        <Typography>Platform: {formData.voice_config.platform?.toUpperCase()}</Typography>
                        <Typography>Voice: {formData.voice_config.voice_name || formData.voice_config.voice_id}</Typography>
                        <Typography>Temperature: {formData.voice_config.temperature}</Typography>
                        <Typography>Max Duration: {formData.voice_config.max_duration} minutes</Typography>
                        <Typography>Retry Attempts: {formData.voice_config.retry_attempts}</Typography>
                        <Typography>Record Call: {formData.voice_config.record_call ? 'Yes' : 'No'}</Typography>
                        <Typography>Call Timeout: {formData.voice_config.call_timeout} seconds</Typography>
                        {formData.voice_config.platform === 'bland_ai' && formData.voice_config.platform_config?.task && (
                          <Typography>Task: {formData.voice_config.platform_config.task}</Typography>
                        )}
                        {formData.voice_config.platform === 'bland_ai' && formData.voice_config.platform_config?.pathway_id && (
                          <Typography>Pathway ID: {formData.voice_config.platform_config.pathway_id}</Typography>
                        )}
                      </>
                    )}

                    {formData.channel === 'chat' && (
                      <>
                        <Typography>Platform: {formData.chat_config.platform}</Typography>
                        <Typography>Priority: {formData.chat_config.priority}</Typography>
                        {formData.chat_config.quick_replies.length > 0 && (
                          <Typography>
                            Quick Replies: {formData.chat_config.quick_replies.map(r => r.text).join(', ')}
                          </Typography>
                        )}
                      </>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Schedule Settings</Typography>
                    <Typography>
                      Timezone: {formData.drip_schedule.timezone}
                    </Typography>
                    <Typography>
                      Time Window: {formData.drip_schedule.start_time} -{' '}
                      {formData.drip_schedule.end_time}
                    </Typography>
                    <Typography>
                      Business Hours Only:{' '}
                      {formData.drip_schedule.business_hours_only ? 'Yes' : 'No'}
                    </Typography>
                    <Typography>
                      Exclude Weekends:{' '}
                      {formData.drip_schedule.exclude_weekends ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>
                      Message Steps:
                    </Typography>
                    {formData.drip_schedule.message_steps.map((step, index) => (
                      <Box key={index} sx={{ ml: 2, mb: 1 }}>
                        <Typography>
                          Step {step.order}:
                        </Typography>
                        <Typography sx={{ ml: 2 }}>
                          • Delay: {step.delay_units} {step.delay_unit_type}
                        </Typography>
                        {formData.channel === 'voice' ? (
                          <>
                            <Typography sx={{ ml: 2 }}>
                              • Platform: {(step.voice_config || step[`${formData.channel}_config`])?.platform?.toUpperCase() || 'BLAND AI'}
                            </Typography>
                            <Typography sx={{ ml: 2 }}>
                              • Voice: {(step.voice_config || step[`${formData.channel}_config`])?.voice_name || (step.voice_config || step[`${formData.channel}_config`])?.voice_id || 'Default'}
                            </Typography>
                            {(step.voice_config || step[`${formData.channel}_config`])?.platform === 'bland_ai' && (step.voice_config || step[`${formData.channel}_config`])?.platform_config?.pathway_id && (
                              <Typography sx={{ ml: 2 }}>
                                • Pathway ID: {(step.voice_config || step[`${formData.channel}_config`]).platform_config.pathway_id}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <>
                            {step.template ? (
                              <Typography sx={{ ml: 2 }}>
                                • Template: {templates.find(t => t.id === step.template)?.name}
                              </Typography>
                            ) : (
                              <Typography sx={{ ml: 2 }}>
                                • Content: {step[`${formData.channel}_config`]?.content || 'No content'}
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                    ))}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Retry Configuration</Typography>
                    <Typography>
                      Retry Strategy: {formData.retry_strategy_id ? `Strategy ID: ${formData.retry_strategy_id}` : 'Default Strategy'}
                    </Typography>
                    <Typography>
                      Max Retries: {formData.max_retries ? formData.max_retries : 'Use strategy default'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Note</Typography>
                    <Typography>
                      After creating the campaign, you will be redirected to the lead selection page where you can configure which leads to include in this campaign.
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/crm/lead-nurturing')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" gutterBottom>
            Create Drip Campaign
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
          )}
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => setCreateDialogOpen(true)}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Campaign'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      >
        <DialogTitle>Create Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Choose how you want to create this campaign:
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleSubmit(false)} color="primary">
            Create as Draft
          </Button>
          <Button onClick={() => handleSubmit(true)} color="success" variant="contained">
            Create & Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Channel Switch Confirmation Dialog */}
      <Dialog
        open={channelSwitchDialogOpen}
        onClose={() => {
          setChannelSwitchDialogOpen(false);
          setPendingChannel(null);
        }}
      >
        <DialogTitle>Switch Channel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Switching channels will reset all message steps and channel-specific configurations.
            This action cannot be undone. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setChannelSwitchDialogOpen(false);
              setPendingChannel(null);
            }}
          >
            Cancel
          </Button>
          {previousChannelState && (
            <Button 
              onClick={revertChannelSwitch}
              color="secondary"
            >
              Revert to Previous Channel
            </Button>
          )}
          <Button 
            onClick={() => switchChannel(pendingChannel)} 
            color="warning" 
            variant="contained"
          >
            Switch Channel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreateDripCampaign; 